import React from 'react';
import { DateTime } from 'src_common/utils/time/time';
import { observer } from 'src/utils/mobx-react';

import { CustomerButton } from 'src/components/FraudAndCompliance/CustomerButton/CustomerButton';
import {
    ColumnHeader,
    InnerTable,
    RowWithTable,
    SectionMessage,
    TableCell,
    TableHeaderRow,
    TableRow,
    TableRowAddColor,
    TableWrapper,
} from './DOBMismatch.style';
import { ChevronDownIconWrapper, Select, SelectWrapper } from './CommonTable.styles';
import { useAppStateContext } from 'src/AppState/AppState';
const ACTIONS = {
    your: [
        { action: 'cancel', name: 'Mark as reviewed' },
        { action: 'unassign', name: 'Unassign' },
    ],
    none: [{ action: 'assign', name: 'Assign to me' }],
    other: [{ action: 'unassign', name: 'Unassign' }],
};

type ActionsType = 'your' | 'none' | 'other';

interface DataListType {
    accountId: number;
    accountName: string;
    assignee: { id: number; type: string; name: string };
    channels: Array<string>;
    createdAt: string;
    details: {
        matchedData: Array<string>;
        types: Array<string>;
        accounts: Array<{ id: number; type: string; name: string }>;
    };
    id: string;
    status: string;
    type: string;
    universeId: string;
}

type DataType = Immutable.Map<string, unknown> | Immutable.List<DataListType>;

interface PropsType {
    type: ActionsType;
    status: string;
    onCancelRequest: (dobMismatch: unknown) => void;
    onAssignRequest: (dobMismatch: unknown) => void;
    onUnassignRequest: (dobMismatch: unknown) => void;

    data: DataType;
}

interface OptionType {
    action: string;
    name: string;
}
const getOption = (state: OptionType, id: string): React.ReactElement => {
    return (
        <option
            key={`${id}-${state.action}`}
            value={state.action}
        >
            {state.name}
        </option>
    );
};

export const DOBMismatch = observer(
    'DOBMismatch',
    ({
        type,
        status,
        onCancelRequest,
        onAssignRequest,
        onUnassignRequest,
        data,
    }: PropsType): React.ReactElement | null => {
        const { models } = useAppStateContext();

        const doAction = (e: React.ChangeEvent<HTMLSelectElement>, dobMismatch: unknown): void => {
            const action = e.target.value;

            if (action === 'unassign') {
                onUnassignRequest(dobMismatch);
            } else if (action === 'assign') {
                onAssignRequest(dobMismatch);
            } else if (action === 'cancel') {
                onCancelRequest(dobMismatch);
            }
        };

        //@ts-expect-error
        const renderRow = (dobMismatch, type: ActionsType): React.ReactNode => {
            const id = dobMismatch.get('id');
            return (
                <TableRow
                    section={type}
                    key={id}
                >
                    <RowWithTable colSpan={6}>
                        <InnerTable>
                            <tbody>
                                <TableRowAddColor>
                                    <TableCell>
                                        {DateTime.from(dobMismatch.get('createdAt'))?.format('DD-MM-YYYY HH:mm') ?? '-'}
                                    </TableCell>
                                    <TableCell>
                                        <CustomerButton
                                            models={models}
                                            customerId={dobMismatch.get('accountId')}
                                        />
                                    </TableCell>
                                    <TableCell>{dobMismatch.getIn(['details', 'firstName'])}</TableCell>
                                    <TableCell>{dobMismatch.getIn(['details', 'surname'])}</TableCell>
                                    <TableCell>{dobMismatch.getIn(['details', 'dob'])}</TableCell>
                                    <TableCell disableColor={true}>
                                        <SelectWrapper>
                                            <Select
                                                name='action'
                                                onChange={(e: React.ChangeEvent<HTMLSelectElement>): void =>
                                                    doAction(e, dobMismatch)
                                                }
                                            >
                                                <option>Choose an action</option>
                                                {ACTIONS[type].map((state) => getOption(state, id))}
                                            </Select>
                                            <ChevronDownIconWrapper />
                                        </SelectWrapper>
                                    </TableCell>
                                </TableRowAddColor>
                            </tbody>
                        </InnerTable>
                    </RowWithTable>
                </TableRow>
            );
        };

        const areNoItems = status === 'FINISHED' && data.size === 0;

        if (areNoItems) {
            return <SectionMessage>There are no items</SectionMessage>;
        }

        return (
            <TableWrapper>
                <thead>
                    <TableHeaderRow>
                        <ColumnHeader>Date Detected</ColumnHeader>
                        <ColumnHeader>Customer</ColumnHeader>
                        <ColumnHeader>First Name</ColumnHeader>
                        <ColumnHeader>Surname</ColumnHeader>
                        <ColumnHeader>Call Credit DOB issued</ColumnHeader>
                        <ColumnHeader>Action</ColumnHeader>
                    </TableHeaderRow>
                </thead>
                <tbody>{data.map((d: unknown) => renderRow(d, type)).toArray()}</tbody>
            </TableWrapper>
        );
    }
);
